import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { lagerLocationsAPI } from '../services/api';
import { LagerLayout, LagerLocation, LagerStorageRow } from '../types';
import { showCustomAlert } from './CustomAlert';

type Props = {
  visible: boolean;
  // Lokacija koja se menja; null = nova lokacija
  location: LagerLocation | null;
  // Početne vrednosti za novu lokaciju (npr. nacrtanu na 3D mapi)
  initialRows?: LagerStorageRow[];
  layout?: LagerLayout | null;
  onHide: () => void;
  onSaved: (location: LagerLocation) => void;
};

const LagerLocationDialog: React.FC<Props> = ({ visible, location, initialRows, layout, onHide, onSaved }) => {
  const [name, setName] = useState('');
  const [rows, setRows] = useState<LagerStorageRow[]>([]);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setName(location?.name || '');
    setRows(location ? location.rows.map((r) => ({ ...r })) : initialRows || [{ name: 'Red 1', capacity: 10 }]);
    setFormError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, location]);

  const updateRow = (index: number, patch: Partial<LagerStorageRow>) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const handleSubmit = async () => {
    if (!name.trim()) return setFormError('Naziv lokacije je obavezan');
    for (const r of rows) {
      if (!r.name.trim()) return setFormError('Svaki red mora imati naziv');
      if (!r.capacity || r.capacity < 1) return setFormError(`Red "${r.name}": broj mesta mora biti veći od 0`);
      if (r.occupied && r.capacity < r.occupied) {
        return setFormError(`Red "${r.name}": broj mesta ne može biti manji od zauzetih (${r.occupied})`);
      }
    }
    const payload = {
      name: name.trim(),
      rows: rows.map((r) => ({ ...(r._id ? { _id: r._id } : {}), name: r.name.trim(), capacity: r.capacity })),
      ...(layout !== undefined ? { layout } : {})
    };
    try {
      setSaving(true);
      setFormError('');
      const saved = location?._id ? await lagerLocationsAPI.update(location._id, payload) : await lagerLocationsAPI.create(payload);
      onSaved(saved);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Greška pri čuvanju lokacije';
      setFormError(msg);
      showCustomAlert.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      header={location ? 'Izmeni lokaciju' : 'Dodaj lokaciju'}
      visible={visible}
      style={{ width: '640px', maxWidth: '96vw' }}
      onHide={onHide}
      modal
      closable
    >
      <div className="p-fluid">
        {formError && (
          <div style={{ marginBottom: '1rem' }}>
            <Message severity="error" text={formError} />
          </div>
        )}

        {layout && (
          <div style={{ marginBottom: '1rem', color: '#64748b' }}>
            <i className="pi pi-map-marker" style={{ marginRight: '0.4rem' }} />
            Površina na mapi: {layout.width.toFixed(1)} × {layout.depth.toFixed(1)} m
          </div>
        )}

        <label htmlFor="locationName" style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>
          Naziv lokacije
        </label>
        <InputText id="locationName" value={name} onChange={(e) => setName(e.target.value)} placeholder="npr. Plato A" autoFocus />

        <div className="flex justify-content-between align-items-center" style={{ marginTop: '1.25rem', marginBottom: '0.5rem' }}>
          <span style={{ fontWeight: 600 }}>Redovi</span>
          <Button
            label="Dodaj red"
            icon="pi pi-plus"
            size="small"
            outlined
            onClick={() => setRows((prev) => [...prev, { name: `Red ${prev.length + 1}`, capacity: prev[prev.length - 1]?.capacity || 10 }])}
            style={{ width: 'auto' }}
          />
        </div>

        {rows.length === 0 && <small style={{ color: '#64748b' }}>Lokacija nema redova.</small>}

        <div style={{ maxHeight: '45vh', overflowY: 'auto', overflowX: 'hidden' }}>
          {rows.map((row, index) => (
            <div key={row._id || `new-${index}`} className="grid align-items-end" style={{ marginBottom: '0.25rem', marginRight: 0 }}>
              <div className="col-12 md:col-6">
                <label style={{ fontSize: '0.85rem', display: 'block', marginBottom: '0.25rem' }}>Naziv reda</label>
                <InputText value={row.name} onChange={(e) => updateRow(index, { name: e.target.value })} />
              </div>
              <div className="col-8 md:col-4">
                <label style={{ fontSize: '0.85rem', display: 'block', marginBottom: '0.25rem' }}>
                  Mesta za palete{row.occupied ? ` (zauzeto ${row.occupied})` : ''}
                </label>
                <InputNumber value={row.capacity} onValueChange={(e) => updateRow(index, { capacity: Number(e.value || 0) })} min={0} useGrouping={false} />
              </div>
              <div className="col-4 md:col-2 flex justify-content-end">
                <Button
                  icon="pi pi-trash"
                  severity="danger"
                  text
                  rounded
                  disabled={Number(row.occupied || 0) > 0}
                  tooltip={Number(row.occupied || 0) > 0 ? 'Red ima zauzeta mesta' : 'Ukloni red'}
                  tooltipOptions={{ showOnDisabled: true }}
                  onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-2 justify-content-end" style={{ marginTop: '1.25rem' }}>
          <Button label="Sačuvaj" icon="pi pi-check" onClick={handleSubmit} loading={saving} />
          <Button label="Otkaži" icon="pi pi-times" severity="secondary" outlined onClick={onHide} />
        </div>
      </div>
    </Dialog>
  );
};

export default LagerLocationDialog;
